// `client-contact-search.page.tpl.html:202-419` — the contact builder's second section.
//
// ⚠️ MOST OF IT IS BEHIND `jobCompanyInfoEnabled`, AND THE TENANT UNDER TEST DOES NOT HAVE IT.
// Categories hiring for, Number of open jobs, Last job opening, Size and Type are all gated on
// that one flag, as is the high-supply checkbox nested inside the hiring-likelihood block. With
// it off — which is what every reference capture of this screen is — the section is three
// controls. Rendering the gated ones unconditionally triples the section's height.
import type { ReactElement } from 'react'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { tagsPlaceholder } from './SkillSetSection.tsx'

export interface CompanyFiltersSectionProps {
  /** `hiringLikelihoodCriteria.highLikelihood`. */
  highHiringLikelihood?: boolean
  /** `supplyCriteria.highSupply` — nested, and only offered with `jobCompanyInfoEnabled`. */
  highSupply?: boolean
  /** `industryCriteria.industries`. */
  industries?: readonly Tag[]
  /** `companyNamesCriteria.companyNames`. */
  companyNames?: readonly Tag[]
  jobCompanyInfoEnabled?: boolean
  /** `High supply in {{::ctrl.atsLabel}}` — the checkbox names the ATS. */
  atsLabel?: string
  onHighHiringLikelihoodChange?: (enabled: boolean) => void
  onHighSupplyChange?: (enabled: boolean) => void
  /** `typeaheads.getExtensionIndustries($query, true)` — the same source the candidate
   *  builder's Work-experience industries use. */
  loadIndustries?: (query: string) => Promise<readonly Tag[]>
  onIndustriesChange?: (tags: readonly Tag[]) => void
  onCompanyNamesChange?: (tags: readonly Tag[]) => void
  /**
   * `sizeCriteria.sizes` and `typeCriteria.types` — the two `jobCompanyInfoEnabled` fields,
   * both `add-from-autocomplete-only`, fed by `/company-search/searchCompanySizes` and
   * `searchCompanyTypes`.
   */
  companySizes?: readonly Tag[]
  companyTypes?: readonly Tag[]
  loadCompanySizes?: (query: string) => Promise<readonly Tag[]>
  loadCompanyTypes?: (query: string) => Promise<readonly Tag[]>
  onCompanySizesChange?: (tags: readonly Tag[]) => void
  onCompanyTypesChange?: (tags: readonly Tag[]) => void
  /** The rest of the high-hiring-likelihood block — same nesting as the supply checkbox. */
  hiringCategories?: readonly Tag[]
  openJobsMin?: number
  lastOpenedJobDaysAgo?: number
  loadHiringCategories?: (query: string) => Promise<readonly Tag[]>
  onHiringCategoriesChange?: (tags: readonly Tag[]) => void
  onOpenJobsMinChange?: (value: number | undefined) => void
  onLastOpenedJobChange?: (value: number | undefined) => void
}

/** `openJobsCountOptionDisplayText` / `lastJobOpeningOptionDisplayText` (component:196, :206).
 *  ⚠️ "Any" IS `undefined`, NOT 0 — the mapper drops the criterion on `isUndefined`, and a 0
 *  would send a real "at least zero open jobs" filter. The three commented-out day options are
 *  upstream's own; they stay commented. */
const OPEN_JOBS_OPTIONS: ReadonlyArray<{ display: string; value?: number }> = [
  { display: 'Any' },
  { display: '5+', value: 5 },
  { display: '10+', value: 10 },
  { display: '20+', value: 20 },
  { display: '50+', value: 50 },
  { display: '100+', value: 100 },
]

const LAST_JOB_OPENING_OPTIONS: ReadonlyArray<{ display: string; value?: number }> = [
  { display: 'Any' },
  { display: 'Last 60 days', value: 60 },
  { display: 'Last 90 days', value: 90 },
  { display: 'Last 180 days', value: 180 },
]

export function CompanyFiltersSection({
  highHiringLikelihood = false,
  highSupply = false,
  industries,
  companyNames,
  jobCompanyInfoEnabled = false,
  atsLabel,
  onHighHiringLikelihoodChange,
  onHighSupplyChange,
  loadIndustries,
  onIndustriesChange,
  onCompanyNamesChange,
  companySizes,
  companyTypes,
  loadCompanySizes,
  loadCompanyTypes,
  onCompanySizesChange,
  onCompanyTypesChange,
  hiringCategories,
  openJobsMin,
  lastOpenedJobDaysAgo,
  loadHiringCategories,
  onHiringCategoriesChange,
  onOpenJobsMinChange,
  onLastOpenedJobChange,
}: CompanyFiltersSectionProps): ReactElement {
  return (
    <>
      <div className="hiring-status-wrap mT10">
        <div className="hiring-likelihood sub-section">
          <div className="hiring-likelihood-container">
            <div className="form-group hiring-likelihood-group mB10">
              {/* ⚠️ THE LABEL ITSELF SWAPS ON THE FLAG: "Company actively hiring" with
                  `jobCompanyInfoEnabled`, "High hiring likelihood" without. Same checkbox, same
                  criteria key, two different sentences — and the reference shows the second. */}
              <WooCheckbox
                checked={highHiringLikelihood}
                onChange={(next) => {
                  onHighHiringLikelihoodChange?.(next)
                }}
              >
                {jobCompanyInfoEnabled ? 'Company actively hiring' : 'High hiring likelihood'}
              </WooCheckbox>
            </div>
          </div>
        </div>
      </div>

      {/* Nested twice over: the supply box needs the flag AND the box above it ticked. */}
      {!jobCompanyInfoEnabled || !highHiringLikelihood ? null : (
        <div className="high-hiring-likelihood-options">
          <div className="supply sub-section">
            <div className="supply-container">
              <div className="form-group mB10">
                <WooCheckbox
                  checked={highSupply}
                  onChange={(next) => {
                    onHighSupplyChange?.(next)
                  }}
                >
                  {atsLabel === undefined ? 'High supply' : `High supply in ${atsLabel}`}
                </WooCheckbox>
              </div>
            </div>
          </div>

          {/* `Categories hiring for` — `getRootTitleClasses`, add-from-autocomplete-only. */}
          <div className="hiring-categories-wrap">
            <div className="hiring-categories sub-section">
              <div className="hiring-categories-container">
                <div className="form-group hiring-categories-group form-group-width-limit">
                  <label className="bold-soft">Categories hiring for</label>
                  <TagsInput
                    name="hiringCategories"
                    className="or-in-between"
                    placeholder="Select a category"
                    tags={hiringCategories ?? []}
                    indicator={hiringCategories === undefined || hiringCategories.length === 0}
                    indicatorClassName="fas fa-search"
                    {...(loadHiringCategories === undefined ? {} : { loadSuggestions: loadHiringCategories })}
                    onAdd={(tag) => {
                      onHiringCategoriesChange?.([...(hiringCategories ?? []), tag])
                    }}
                    onRemove={(index) => {
                      onHiringCategoriesChange?.((hiringCategories ?? []).filter((_, at) => at !== index))
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="open-jobs-wrap">
            <div className="open-jobs sub-section">
              <div className="open-jobs-container">
                <div className="form-group open-jobs-group">
                  <label className="bold-soft">Number of open jobs</label>
                  <div className="select">
                    <select
                      className="form-control input"
                      value={openJobsMin === undefined ? '' : String(openJobsMin)}
                      onChange={(event) => {
                        onOpenJobsMinChange?.(event.target.value === '' ? undefined : Number(event.target.value))
                      }}
                    >
                      {OPEN_JOBS_OPTIONS.map((option) => (
                        <option key={option.display} value={option.value === undefined ? '' : String(option.value)}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="last-job-opening-wrap">
            <div className="last-job-opening sub-section">
              <div className="last-job-opening-container">
                <div className="form-group last-job-opening-group">
                  <label className="bold-soft">Last job opening</label>
                  <div className="select">
                    <select
                      className="form-control input"
                      value={lastOpenedJobDaysAgo === undefined ? '' : String(lastOpenedJobDaysAgo)}
                      onChange={(event) => {
                        onLastOpenedJobChange?.(event.target.value === '' ? undefined : Number(event.target.value))
                      }}
                    >
                      {LAST_JOB_OPENING_OPTIONS.map((option) => (
                        <option key={option.display} value={option.value === undefined ? '' : String(option.value)}>
                          {option.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ⚠️ THREE NESTED DIVS, AND THE MIDDLE ONE IS THE FLEX ROW. `industries-container` is
          `display: flex` and `industries-size` carries the 656px triple (`flex`, `max-width`,
          `min-width`) — flatten them to one `form-group` and the field stretches to the panel,
          which at 1146px is 125px too wide. */}
      <div className="industries-criteria sub-section">
        <div className="industries-container">
          <div className="form-group industries-size">
            <label className="bold-soft">Industry</label>
            <TagsInput
              // ⚠️ `name` ON THE ELEMENT. Upstream's `name="industries"` is what the form's
              // own error label keys off (`ctrl.form.industries.$error.leftoverText`), and
              // the two contact fields were the only tag inputs in either builder rendering
              // without one — they came up `(unnamed)` against production's named pair.
              name="industries"
              className="or-in-between"
              placeholder={tagsPlaceholder(industries, 'industry')}
              tags={industries ?? []}
              indicator={industries === undefined || industries.length === 0}
              indicatorClassName="fas fa-search"
              {...(loadIndustries === undefined ? {} : { loadSuggestions: loadIndustries })}
              onAdd={(tag) => {
                onIndustriesChange?.([...(industries ?? []), tag])
              }}
              onRemove={(index) => {
                onIndustriesChange?.((industries ?? []).filter((_, at) => at !== index))
              }}
            />
          </div>
        </div>
      </div>

      {/* ⚠️ AFTER INDUSTRY, BEFORE COMPANY NAME — the template's order, and the first
          placement had them above Industry. The section is a flex column with no `order`
          styles, so source order IS screen order: production reads
          Industry / Size / Type / Company name and ours read Size / Type / Industry, which
          the round-trip tag list showed as a different sequence of the same four values. */}
      {/* ⚠️ BOTH FIELDS ARE `ng-if="ctrl.jobCompanyInfoEnabled"` AND NEITHER WAS PORTED. The
          flag was hardcoded off here under a note saying the view model does not carry the
          capability — it does, as `featureFlags.JOB_COMPANY_INFO`. With it on, production
          renders Size and Type between the hiring options and Industry, and the saved alert
          carries `sizeCriteria` / `typeCriteria`; this form had no way to produce either. */}
      {!jobCompanyInfoEnabled ? null : (
        <>
          <div className="company-size-wrap">
            <div className="company-size sub-section">
              <div className="company-size-container">
                <div className="form-group company-size-group form-group-width-limit">
                  <label className="bold-soft">Size</label>
                  <TagsInput
                    name="companySize"
                    className="or-in-between"
                    placeholder="Select a company size"
                    tags={companySizes ?? []}
                    indicator={companySizes === undefined || companySizes.length === 0}
                    indicatorClassName="fas fa-search"
                    {...(loadCompanySizes === undefined ? {} : { loadSuggestions: loadCompanySizes })}
                    onAdd={(tag) => {
                      onCompanySizesChange?.([...(companySizes ?? []), tag])
                    }}
                    onRemove={(index) => {
                      onCompanySizesChange?.((companySizes ?? []).filter((_, at) => at !== index))
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="company-type-wrap">
            <div className="company-type sub-section">
              <div className="company-type-container">
                <div className="form-group company-type-group form-group-width-limit">
                  <label className="bold-soft">Type</label>
                  <TagsInput
                    name="companyType"
                    className="or-in-between"
                    placeholder="Select a company type"
                    tags={companyTypes ?? []}
                    indicator={companyTypes === undefined || companyTypes.length === 0}
                    indicatorClassName="fas fa-search"
                    {...(loadCompanyTypes === undefined ? {} : { loadSuggestions: loadCompanyTypes })}
                    onAdd={(tag) => {
                      onCompanyTypesChange?.([...(companyTypes ?? []), tag])
                    }}
                    onRemove={(index) => {
                      onCompanyTypesChange?.((companyTypes ?? []).filter((_, at) => at !== index))
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="company-name-criteria sub-section">
        <div className="company-name-container">
          <div className="form-group company-name-size">
            <label className="bold-soft">Company name</label>
            {/* ⚠️ NO MAGNIFIER AND NO AUTOCOMPLETE-ONLY HERE. A recruiter may search for a
                company Woo has never indexed, so this is one of the two free-text tag fields in
                either builder — and the missing indicator is visible in the reference: Industry
                above it has the search icon and this one does not. */}
            <TagsInput
              name="companyName"
              className="or-in-between"
              placeholder={tagsPlaceholder(companyNames, 'company name')}
              tags={companyNames ?? []}
              freeText
              onAdd={(tag) => {
                onCompanyNamesChange?.([...(companyNames ?? []), tag])
              }}
              onRemove={(index) => {
                onCompanyNamesChange?.((companyNames ?? []).filter((_, at) => at !== index))
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}
